import React, { useCallback, useEffect } from 'react';
import moment from 'moment';
import { Helmet } from 'react-helmet-async';

import { useGlobal } from 'models/global';
import { useLocale } from 'models/locale';
import { useRouting } from 'models/routing';
import { useCountryCodes } from 'models/countryCodes';
import { useNationalities } from 'models/nationalities';
import { useAuth, useGiantVid } from 'models/auth';

import { localeParser } from 'util/helper';
import { initLineSDK } from 'util/sdk';
import { lanList } from 'util/i18n/config';
import { appPath } from 'util/routingConfig';
import ogImage from 'images/og_image.jpg';

import styles from './index.css';

const Root: React.FC = ({ children }) => {
	const [{ pathname }] = useRouting();

	const defaultTitle = '捷安特旅遊｜全台最專業單車旅遊品牌｜環島・花東・EBIKE 體驗';
	const defaultDescription =
		'捷安特旅遊提供全包式單車旅行、單車租賃與活動課程：環島、花東、EBIKE 北進武嶺等行程，含路線規劃、住宿餐食安排、專業領隊與保母車支援，安心出發享受騎乘。';
	const currentUrl =
		typeof window !== 'undefined'
			? window.location.href
			: `${process.env.SELF_HOST_ENDPOINT ?? ''}${pathname}`;
	const [
		{
			currentLocCode,
			locales: {
				data: { byCode: locByCode },
			},
		},
		{ fetchLocaleList, updateCurrentLocale },
	] = useLocale();
	const [{ isInitialed }, { setInitialed, setInitial, initialSetting }] = useGlobal();
	const [, { getAuthProcess }] = useAuth();
	const [, { getCountryCodes }] = useCountryCodes();
	const [, { getNationalities }] = useNationalities();
	const [, { getVid }] = useGiantVid();

	const asyncInitialSetting = useCallback(async () => {
		console.log('Root Mounted Start -----------------------');

		setInitial();

		getVid(); // 取得 Giant 訪客 ID

		await fetchLocaleList(); // 取得 locale 列表
		if (!window.location.pathname.includes(appPath.authCallback)) {
			await getAuthProcess(); // 會員登入流程
		}

		await initialSetting(); // 初始化網站（判斷 locale， 拿 config）

		getCountryCodes(); // 取得手機國碼
		getNationalities(); // 取得國籍列表

		initLineSDK();
		setInitialed();

		console.log('Root Mounted Ended -----------------------');
	}, []);

	useEffect(() => {
		asyncInitialSetting();
	}, [asyncInitialSetting]);

	useEffect(() => {
		if (isInitialed) {
			const { valid, locale } = localeParser(locByCode);

			/* URL 改變時，偵測 Locale 是否改變。若有，則重新 init 網站 */
			if (valid && currentLocCode !== locale.code) {
				updateCurrentLocale(locale.code as string);
				asyncInitialSetting();
			}
		}
	}, [pathname, isInitialed]);

	return (
		<div className={styles.root}>
			<Helmet>
				<title>{defaultTitle}</title>
				<meta name="description" content={defaultDescription} />
				<meta property="og:site_name" content="Giant Adventure-捷安特旅遊" />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={defaultTitle} />
				<meta property="og:description" content={defaultDescription} />
				<meta property="og:locale" content="zh_TW" />
				<meta property="og:url" content={currentUrl} />
				<meta property="og:image" content={`${process.env.SELF_HOST_ENDPOINT}${ogImage}`} />
			</Helmet>

			{isInitialed ? children : null}
		</div>
	);
};

export default Root;
